import { DateTime } from 'luxon';
import { getDisplayTimezone } from '@/features/experiences/lib/experience';
import { EventListDisplay } from '@/types/gql.generated';
import { parseIsoDate } from '@/utils/dates';
import {
  forwardedViewOptions,
  groupEvents,
  prepareAndExpandEvents,
} from '../../groupEvents';
import { toPrintableLayout } from '../../printableConfigAdapter';
import { sortEventsForDay } from '../../sortEvents';
import type { PrintableTemplate } from '../../types';
import type { GroupData, MonthData, PageData } from './types';

export const expandAndGroupByMonth: PrintableTemplate<PageData>['group'] = ({
  events,
  experience,
  options,
}) => {
  const layout = toPrintableLayout(experience.layoutId);

  // Only honor experience-level custom dates when display mode is Custom -
  // other display modes may leave stale values on these fields.
  let monthExpandedStartDateStr: string | undefined = undefined;
  let monthExpandedEndDateStr: string | undefined = undefined;
  if (experience.eventListDisplay === EventListDisplay.Custom) {
    const customStart = parseIsoDate(experience.eventListStartDate);
    const customEnd = parseIsoDate(experience.eventListEndDate);

    if (customStart && customEnd) {
      // Expand to full-month boundaries so partial months render completely.
      monthExpandedStartDateStr =
        customStart.startOf('month').toISODate() ?? undefined;
      monthExpandedEndDateStr =
        customEnd.endOf('month').toISODate() ?? undefined;
    } else if (customStart) {
      monthExpandedStartDateStr =
        customStart.startOf('month').toISODate() ?? undefined;
    } else if (customEnd) {
      monthExpandedEndDateStr =
        customEnd.endOf('month').toISODate() ?? undefined;
    }
  }

  const resolvedStartDate =
    options?.eventListStartDate ?? monthExpandedStartDateStr;
  const resolvedEndDate = options?.eventListEndDate ?? monthExpandedEndDateStr;

  const preparedEvents = prepareAndExpandEvents(events, {
    layout,
    expandRecurrences: true,
    expandMultiDay: true,
    expandMultiDayToAllDays: true,
    decorateMultiDay: true,
    eventListStartDate: resolvedStartDate,
    eventListEndDate: resolvedEndDate,
    ...forwardedViewOptions(options),
  });

  // Group by day with month expansion
  const grouped = groupEvents(preparedEvents, 'y-DDD', 'y-DDD', true, {
    ...options,
    layout,
    includeEmptyGroups: true,
    expandFullyForDuration: 'month',
    eventListStartDate: resolvedStartDate,
    eventListEndDate: resolvedEndDate,
  });

  // Convert to GroupData boxes and sort events within each day
  const boxes: GroupData[] = grouped.map((group, index): GroupData => ({
    boxIndex: index,
    groupIndex: index,
    date: group.date,
    boxIndexForGroup: 0,
    boxCountForGroup: 1,
    events: sortEventsForDay(group.events, experience),
  }));

  // Group boxes by month
  const monthMap = boxes.reduce<Map<string, MonthData>>((acc, box) => {
    const monthStart = box.date.startOf('month');
    const key = `${monthStart.year}-${String(monthStart.month).padStart(2, '0')}`;
    const existing = acc.get(key);
    if (existing) {
      existing.boxes.push(box);
    } else {
      acc.set(key, {
        month: { month: monthStart.month, year: monthStart.year },
        boxes: [box],
      });
    }
    return acc;
  }, new Map());

  const fallbackMonth = DateTime.local({
    zone: getDisplayTimezone(experience),
  });
  const months =
    monthMap.size > 0
      ? [...monthMap.values()]
      : [
          {
            month: { month: fallbackMonth.month, year: fallbackMonth.year },
            boxes: [],
          },
        ];

  return {
    pageIndexForMonth: 0,
    months,
  };
};
